// Detailed-mode overlay for the design page: a bottom-center toggle pill plus a
// glass card that explains the clicked element. The card reads the engine's
// machine-verified rationale (attrs.rationale): a placement reason and a colour-
// coded PASS/FAIL rule checklist (always present, free). The GPT-5.5 Persian
// narrative is NOT produced during the design run anymore (that made the run take
// minutes); instead a "تحلیل" button generates it on demand for THIS element only
// and caches it. Everything shown is the engine's ground truth; a failed check is
// surfaced in red, never hidden.

import { useState } from 'react';
import { explainDesignElement } from '../../lib/designApi';

const ELEMENT_FA = {
  olt: 'OLT', odc: 'ODC', mfat_l2: 'MFAT', fat: 'FAT',
  mfat: 'MFAT', sfat: 'FAT',   // retired legacy per-FAT labels (historical rows)
  hh: 'هندهول', tconnector: 'T-کانکتور', hconnector: 'H-کانکتور',
};
const STATUS = {
  pass: { color: '#34d399', mark: '✓' },
  fail: { color: '#f87171', mark: '✗' },
  info: { color: '#94a3b8', mark: '•' },
};

// Persian labels for the per-building CAD attributes (Dowlatabad municipal data).
const FACADE_FA = {
  none: 'بدون نما', cement: 'سیمان', stone: 'سنگ‌پلاک', brick: 'آجر',
  composite: 'کامپوزیت', glass: 'شیشه', other: 'سایر',
};
const LANDUSE_FA = {
  residential: 'مسکونی', commercial: 'تجاری', industrial: 'صنعتی',
  educational: 'آموزشی', religious: 'مذهبی', administrative: 'اداری',
  medical: 'درمانی', sports: 'ورزشی', cultural: 'فرهنگی و هنری',
  recreational: 'تفریحی و گردشگری', utilities: 'تأسیسات شهری', transport: 'حمل و نقل و انبار',
  green: 'پارک و فضای سبز', agriculture: 'کشاورزی و باغات', natural: 'طبیعی',
  vacant: 'بایر', historic: 'تاریخی',
};
const UNIT_SOURCE_FA = {
  estimated: 'تخمینی (از طبقات)', stored: 'ذخیره‌شده', operator: 'واردشده توسط اپراتور', postal: 'پستی',
};

function pill(active) {
  return {
    padding: '8px 16px', borderRadius: 999, cursor: 'pointer', fontWeight: 700, fontSize: 12.5,
    fontFamily: "'Vazirmatn', sans-serif", direction: 'rtl',
    color: active ? '#0b1220' : '#cbd5e1',
    background: active ? 'linear-gradient(135deg,#2dd4bf,#0d9488)' : 'rgba(15,23,42,0.92)',
    border: `1px solid ${active ? 'rgba(45,212,191,0.45)' : 'rgba(148,163,184,0.28)'}`,
    boxShadow: '0 8px 24px rgba(2,6,23,0.5), 0 1px 3px rgba(0,0,0,0.45)',
    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
  };
}

const cardWrap = {
  position: 'absolute', bottom: 60, left: '50%', transform: 'translateX(-50%)', zIndex: 9,
  width: 'min(380px, calc(100vw - 24px))', direction: 'rtl', fontFamily: "'Vazirmatn', sans-serif",
  background: 'linear-gradient(135deg, rgba(15,23,42,0.97), rgba(30,41,59,0.96))',
  border: '1px solid rgba(148,163,184,0.24)', borderRadius: 14, padding: 14,
  boxShadow: '0 16px 40px rgba(2,6,23,0.62), 0 1px 3px rgba(0,0,0,0.5)',
  backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)',
  maxHeight: '46vh', overflowY: 'auto',
};

function LockChip() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 7px', borderRadius: 999,
      fontSize: 10.5, fontWeight: 700, color: '#5eead4', background: 'rgba(45,212,191,0.14)',
      border: '1px solid rgba(45,212,191,0.4)', whiteSpace: 'nowrap' }}>🔒 قفل</span>
  );
}

// One-line hint explaining the lock interaction (the cursor would otherwise swap
// the card while travelling to its buttons).
function LockFooter({ locked }) {
  return (
    <div style={{ marginTop: 9, fontSize: 10.5, color: '#94a3b8', lineHeight: 1.6,
      borderTop: '1px solid rgba(148,163,184,0.14)', paddingTop: 7 }}>
      {locked
        ? 'این کارت قفل است؛ برای چرخشِ آزاد روی نقشه، × را بزنید.'
        : 'برای قفل‌کردنِ کارت روی این المان کلیک کنید.'}
    </div>
  );
}

function ElementCard({ element, projectId, locked, onClose, editMode, onDelete }) {
  const r = (element.attrs || {}).rationale || {};
  const checks = r.checks || [];
  const failed = checks.filter((c) => c.status === 'fail');
  const typeFa = ELEMENT_FA[element.type] || element.type;
  // The deterministic rationale (reason + checks) is always shown. The GPT prose
  // is generated on demand: seed from any cached value, then fill via the button.
  const [explanation, setExplanation] = useState(r.explanation || null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeErr, setAnalyzeErr] = useState(null);
  const canAnalyze = Boolean(projectId && (r.reason || checks.length));

  const analyze = async () => {
    setAnalyzing(true);
    setAnalyzeErr(null);
    const { explanation: text, error } = await explainDesignElement(projectId, element.ext_id);
    setAnalyzing(false);
    if (error || !text) { setAnalyzeErr(error || 'تحلیل تولید نشد'); return; }
    setExplanation(text);
  };

  return (
    <div style={cardWrap}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <div style={{ fontWeight: 800, fontSize: 13.5, color: '#f1f5f9', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{typeFa} <span style={{ color: '#64748b', fontWeight: 600, fontSize: 11.5 }}>{element.ext_id ?? element.id}</span></span>
          {locked && <LockChip />}
        </div>
        <button type="button" onClick={onClose} aria-label="بستن" title={locked ? 'بستن و آزادسازیِ چرخش' : 'بستن'}
          style={{ width: 26, height: 24, borderRadius: 7, cursor: 'pointer', color: '#cbd5e1', fontSize: 13,
            background: 'rgba(51,65,85,0.7)', border: '1px solid rgba(148,163,184,0.3)' }}>×</button>
      </div>

      {editMode && (
        <button type="button" onClick={() => onDelete && onDelete(element.ext_id ?? element.id)}
          style={{ width: '100%', padding: '8px 12px', borderRadius: 9, fontWeight: 700, fontSize: 12.5,
            fontFamily: "'Vazirmatn', sans-serif", cursor: 'pointer', color: '#fff', marginBottom: 10,
            background: 'linear-gradient(135deg,#dc2626,#991b1b)', border: '1px solid rgba(248,113,113,0.4)' }}>
          حذف این عنصر
        </button>
      )}

      {r.reason && (
        <div style={{ fontSize: 12.5, lineHeight: 1.85, color: '#e2e8f0', marginBottom: checks.length ? 10 : 0 }}>
          {r.reason}
        </div>
      )}

      {checks.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, borderTop: '1px solid rgba(148,163,184,0.18)', paddingTop: 9 }}>
          {checks.map((c, i) => {
            const st = STATUS[c.status] || STATUS.info;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 11.8 }}>
                <span style={{ color: '#cbd5e1' }}>
                  <span style={{ color: st.color, fontWeight: 800, marginInlineEnd: 5 }}>{st.mark}</span>{c.label}
                </span>
                <span style={{ color: '#e2e8f0', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {String(c.actual)} <span style={{ color: '#64748b' }}>({c.limit})</span>
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700,
        color: failed.length ? '#f87171' : '#86efac' }}>
        {failed.length
          ? `نقضِ قانون: ${failed.length} مورد - نیازمندِ اصلاحِ الگوریتم`
          : (checks.length ? 'منطبق با قوانین ✓' : '')}
      </div>

      {!editMode && (explanation ? (
        <div style={{ marginTop: 10, fontSize: 12.3, lineHeight: 1.95, color: '#cbd5e1',
          borderTop: '1px solid rgba(148,163,184,0.18)', paddingTop: 9, whiteSpace: 'pre-wrap' }}>
          {explanation}
        </div>
      ) : canAnalyze && (
        <div style={{ marginTop: 10, borderTop: '1px solid rgba(148,163,184,0.18)', paddingTop: 10 }}>
          <button type="button" onClick={analyze} disabled={analyzing}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 9, fontWeight: 700, fontSize: 12.5,
              fontFamily: "'Vazirmatn', sans-serif", cursor: analyzing ? 'wait' : 'pointer', color: '#0b1220',
              background: analyzing ? 'rgba(45,212,191,0.55)' : 'linear-gradient(135deg,#2dd4bf,#0d9488)',
              border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
            {analyzing ? 'در حال تحلیل با هوش مصنوعی…' : '✨ تحلیلِ هوش مصنوعی'}
          </button>
          {analyzeErr && (
            <div style={{ marginTop: 7, fontSize: 11.5, color: '#f87171' }}>{analyzeErr}</div>
          )}
          {!analyzing && !analyzeErr && (
            <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>
              توضیحِ فارسیِ این المان بر پایهٔ قوانینِ بررسی‌شده تولید می‌شود (چند ثانیه طول می‌کشد).
            </div>
          )}
        </div>
      ))}

      {editMode ? (
        <div style={{ marginTop: 9, fontSize: 10.5, color: '#94a3b8', lineHeight: 1.6,
          borderTop: '1px solid rgba(148,163,184,0.14)', paddingTop: 7 }}>
          این عنصر را روی نقشه بکشید تا جابه‌جا شود، یا «حذف این عنصر» را بزنید.
        </div>
      ) : (
        <LockFooter locked={locked} />
      )}
    </div>
  );
}

function Row({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, padding: '3px 0' }}>
      <span style={{ color: '#94a3b8' }}>{label}</span>
      <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function BuildingCard({ data, locked, onClose }) {
  return (
    <div style={cardWrap}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <div style={{ fontWeight: 800, fontSize: 13.5, color: '#f1f5f9', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>ساختمان</span>{locked && <LockChip />}
        </div>
        <button type="button" onClick={onClose} aria-label="بستن" title={locked ? 'بستن و آزادسازیِ چرخش' : 'بستن'}
          style={{ width: 26, height: 24, borderRadius: 7, cursor: 'pointer', color: '#cbd5e1', fontSize: 13,
            background: 'rgba(51,65,85,0.7)', border: '1px solid rgba(148,163,184,0.3)' }}>×</button>
      </div>
      <Row label="کاربری" value={LANDUSE_FA[data.land_use] || data.land_use} />
      <Row label="تعدادِ طبقات" value={data.floor_count} />
      <Row label="تعدادِ واحد" value={data.unit_count} />
      <Row label="جنسِ نما" value={FACADE_FA[data.facade] || data.facade} />
      <Row label="منبعِ تخمینِ واحد" value={UNIT_SOURCE_FA[data.unit_source] || data.unit_source} />
      <Row label="نقطه‌ی ورود" value={data.has_entry ? 'ثبت‌شده' : 'ثبت‌نشده'} />
      {(data.floor_count == null && data.unit_count == null
        && data.land_use == null && data.facade == null) && (
        <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 6 }}>
          برای این ساختمان داده‌ای ثبت نشده است.
        </div>
      )}
      <LockFooter locked={locked} />
    </div>
  );
}

export default function DesignDetailOverlay({ active, hasResult, onToggle, info, projectId, locked, onClose, editMode = false, onDelete }) {
  if (!hasResult) return null;
  const showCard = (active || editMode) && info;
  return (
    <>
      {showCard && info.kind === 'element' && info.element && (
        // Key by edit identity so switching fresh pins also resets card state.
        <ElementCard key={info.element.ext_id ?? info.element.id} element={info.element} projectId={projectId}
          locked={locked} onClose={onClose} editMode={editMode} onDelete={onDelete} />
      )}
      {showCard && info.kind === 'building' && info.data && (
        <BuildingCard key={info.data.id} data={info.data} locked={locked} onClose={onClose} />
      )}
      {!editMode && (
        <div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 8 }}>
          <button type="button" onClick={onToggle} style={pill(active)}>
            {active ? '● حالتِ تفصیلی روشن' : 'حالتِ تفصیلی'}
          </button>
        </div>
      )}
    </>
  );
}
